// WsAdapterClient — the per-adapter WebSocket wire-protocol abstraction.
//
// The generic WS console (档1) speaks the wire raw: it sends/receives frames verbatim and
// never needs to understand the upstream framing. The playgrounds (档2, e.g. Volcengine ASR)
// DO need to build/parse binary protocol frames — that logic is collected behind this
// interface so each adapter's framing lives in exactly one place and the UI stays dumb.
//
// A WsAdapterClient exposes:
//   name            — adapter id (matches wsConfig.adapter / GET /ws/adapters name)
//   clientProtocol  — 'binary' | 'text' (mirrors the adapter meta)
//   buildConfigFrame(opts)  → Promise<ArrayBuffer | string | null>  — first frame after open
//   encodeAudio(pcm, { seq, last }) → Promise<ArrayBuffer>          — one upstream data frame
//   decodeResult(data)      → Promise<{ text?, durationMs?, error?, raw }>  — parse downstream
//
// gzip is async in the browser (Compression Streams API), so every method returns a Promise.
// Adapters that are pure text (e.g. a future OpenAI Realtime client) can implement
// buildConfigFrame/decodeResult with JSON strings and leave encodeAudio unused.
//
// This module is framework-agnostic (no React) so it can be unit-tested in isolation.

import { gzip, gunzip, gzipSupported } from './gzip.js';
import { DoubaoRealtimeClient } from './ws-doubao-client.js';

// ---- byte helpers (browser: no Node Buffer) ------------------------------------------
const concatBytes = (...arrs) => {
  const total = arrs.reduce((n, a) => n + a.byteLength, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(new Uint8Array(a), off); off += a.byteLength; }
  return out;
};

const toArrayBuffer = (u8) =>
  u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);

const hexPreview = (buf, max = 32) => {
  const src = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer || buf);
  const slice = src.subarray(0, max);
  const hex = Array.from(slice).map((b) => b.toString(16).padStart(2, '0')).join(' ');
  return src.length > max ? hex + ' …' : hex;
};

// =====================================================================================
// Volcengine (豆包/火山) streaming ASR — binary framing.
//
// Ported from xapi-backend/tools/ws-spike/demo-asr.mjs. Frame layout (big-endian):
//   header(4B): [0x11, (msgType<<4)|flags, serialization, reserved]
//   [sequence(4B, int32)]  — present when flags & 0x03 (positive; negative on last)
//   payloadSize(4B, uint32)
//   payload(payloadSize)   — gzip-compressed JSON (config) or gzip-compressed PCM (audio)
// msgType: 1=client config, 2=client audio, 9=SERVER_FULL_RESPONSE, 15=error.
// =====================================================================================
export class VolcAsrClient {
  constructor() {
    this.name = 'volcengine-asr';
    this.clientProtocol = 'binary';
  }

  isSupported() { return gzipSupported(); }

  // First frame: full-client-request config (msgType=1, flags=0b0001, seq=1), gzipped JSON.
  //
  // `request` carries the caller-tunable recognition params (enable_punc / enable_itn /
  // enable_ddc / show_utterances / result_type / …) that the Try-It config panel collects
  // from the adapter's `userParams` schema. The gateway forwards uplink frames verbatim, so
  // whatever lands here reaches Volcengine directly. `sampleRate` MUST match the PCM actually
  // sent (the resampler target), otherwise the upstream just gets garbage.
  async buildConfigFrame({ sampleRate = 16000, bits = 16, channels = 1, uid = 'browser', modelName = 'bigmodel', request = {} } = {}) {
    const configJson = {
      user: { uid },
      audio: { format: 'pcm', rate: sampleRate, bits, channel: channels, codec: 'raw' },
      // model_name is an adapter constant — Volcengine's "model" is the endpoint (upstreamUrl),
      // not a caller knob, so it's fixed here and never surfaced in the config panel. enable_punc
      // keeps a sane default; the panel overrides the recognition params via `request`.
      request: { model_name: modelName, enable_punc: true, ...request },
    };
    const payload = await gzip(new TextEncoder().encode(JSON.stringify(configJson)));
    return toArrayBuffer(this._frame(1, 0b0001, 1, payload));
  }

  // Audio frame: msgType=2. Non-last flags=0b0001 seq positive; last flags=0b0011 seq negative.
  async encodeAudio(pcm, { seq = 1, last = false } = {}) {
    const bytes = pcm instanceof Uint8Array ? pcm : new Uint8Array(pcm);
    const payload = await gzip(bytes);
    return toArrayBuffer(this._frame(2, last ? 0b0011 : 0b0001, last ? -seq : seq, payload));
  }

  // Parse a downstream frame. Returns { text?, durationMs?, error?, raw }.
  async decodeResult(data) {
    const u8 = data instanceof Uint8Array ? data : new Uint8Array(data);
    if (u8.length < 4) return { raw: null };
    const msgType = u8[1] >> 4;
    const flags = u8[1] & 0x0f;
    const comp = u8[2] & 0x0f;
    const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);

    // Error frame (msgType=15): [header][code(4B)][message…]
    if (msgType === 15) {
      const code = view.getUint32(4, false);
      const msg = new TextDecoder().decode(u8.subarray(12));
      return { error: { code, message: msg }, raw: { code, message: msg } };
    }

    let off = 4;
    if (flags & 0x03) off += 4; // sequence present
    if (off + 4 > u8.length) return { raw: null };
    const size = view.getUint32(off, false); off += 4;
    let payload = u8.subarray(off, off + size);
    if (comp === 1) { try { payload = await gunzip(payload); } catch { /* leave as-is */ } }
    let json;
    try { json = JSON.parse(new TextDecoder().decode(payload)); } catch { return { raw: null }; }

    return {
      text: json.result && json.result.text ? json.result.text : undefined,
      durationMs: json.audio_info && json.audio_info.duration ? json.audio_info.duration : undefined,
      raw: json,
    };
  }

  // header(4B) + seq(4B) + size(4B) + payload → Uint8Array
  _frame(msgType, flags, seq, payload) {
    // serialization byte: config(1) uses 0x11 (JSON), audio(2) uses 0x01 (raw).
    const header = new Uint8Array([0x11, (msgType << 4) | flags, msgType === 1 ? 0x11 : 0x01, 0x00]);
    const seqBuf = new Uint8Array(4);
    new DataView(seqBuf.buffer).setInt32(0, seq, false);
    const sizeBuf = new Uint8Array(4);
    new DataView(sizeBuf.buffer).setUint32(0, payload.byteLength, false);
    return concatBytes(header, seqBuf, sizeBuf, payload);
  }
}

// Registry: adapter name → client factory. 档2 playgrounds look up here; unknown adapters
// (or text-protocol adapters) fall back to the generic console (档1).
// doubao-realtime 的 client 定义在独立文件(依赖 gzip + 本模块 wsBytes)，此处懒加载注册。
const REGISTRY = {
  'volcengine-asr': () => new VolcAsrClient(),
  'doubao-realtime': () => new DoubaoRealtimeClient(),
};

export function getWsAdapterClient(adapterName) {
  const make = REGISTRY[adapterName];
  return make ? make() : null;
}

export const wsBytes = { hexPreview, concatBytes, toArrayBuffer };
